import { UseMutationOptions, useMutation, useQueryClient } from 'react-query';
import { getUser } from '@frontend/auth-helpers';
import { HTTPBoundSchemaMethod, SchemaIO } from '@frontend/schema';
import { SchemaAuthService } from './api';
import { queryKeys } from './queries';

type SchemaMutationOptions<T extends HTTPBoundSchemaMethod<any>> = UseMutationOptions<
  SchemaIO<T>['output'],
  unknown,
  SchemaIO<T>['input'],
  unknown
>;

export const useAddContact = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['AddContact']> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'AddContact',
    mutationFn: SchemaAuthService.AddContact,
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries(queryKeys.userDetails(getUser()?.userID ?? ''));
      opts?.onSuccess?.apply(this, args);
    },
  });
};

export const useEnableMFA = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['EnableMFA']> = {}) =>
  useMutation({
    mutationKey: 'EnableMFA',
    mutationFn: SchemaAuthService.EnableMFA,
    ...opts,
  });

export const useDisableMFA = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['DisableMFA']> = {}) =>
  useMutation({
    mutationKey: 'DisableMFA',
    mutationFn: SchemaAuthService.DisableMFA,
    ...opts,
  });

export const useValidateContact = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['ValidateContact']> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'ValidateContact',
    mutationFn: SchemaAuthService.ValidateContact,
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries(queryKeys.userDetails(getUser()?.userID ?? ''));
      opts?.onSuccess?.apply(this, args);
    },
  });
};

export const useDeleteContact = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['DeleteContact']> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'DeleteContact',
    mutationFn: SchemaAuthService.DeleteContact,
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries(queryKeys.userDetails(getUser()?.userID ?? ''));
      opts?.onSuccess?.apply(this, args);
    },
  });
};

export const useMakeContactPrimary = (
  opts: SchemaMutationOptions<(typeof SchemaAuthService)['MakeContactPrimary']> = {}
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'MakeContactPrimary',
    mutationFn: SchemaAuthService.MakeContactPrimary,
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries(queryKeys.userDetails(getUser()?.userID ?? ''));
      opts?.onSuccess?.apply(this, args);
    },
  });
};

export const useResendContactValidation = (
  opts: SchemaMutationOptions<(typeof SchemaAuthService)['ResendContactValidation']> = {}
) =>
  useMutation({
    mutationKey: 'ResendContactValidation',
    mutationFn: SchemaAuthService.ResendContactValidation,
    ...opts,
  });

export const useCreateImpression = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['CreateImpression']> = {}) =>
  useMutation({
    mutationKey: 'CreateImpression',
    mutationFn: SchemaAuthService.CreateImpression,
    ...opts,
  });
